// src/theme/ColorModeProvider.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from './createAppTheme';
import {
    COLOR_MODE_AUTH_EVENT,
    getColorModeUserKey,
    readStoredColorMode,
    writeStoredColorMode,
} from './colorModeStorage';

const ColorModeContext = createContext({
    mode: 'light',
    isDark: false,
    toggleColorMode: () => {},
    setColorMode: () => {},
});

export function useColorMode() {
    return useContext(ColorModeContext);
}

export function ColorModeProvider({ children }) {
    const [userKey, setUserKey] = useState(() => getColorModeUserKey());
    const [mode, setMode] = useState(() => readStoredColorMode(getColorModeUserKey()));

    useEffect(() => {
        const syncUser = () => {
            const nextKey = getColorModeUserKey();
            setUserKey(nextKey);
            setMode(readStoredColorMode(nextKey));
        };
        window.addEventListener(COLOR_MODE_AUTH_EVENT, syncUser);
        window.addEventListener('storage', syncUser);
        return () => {
            window.removeEventListener(COLOR_MODE_AUTH_EVENT, syncUser);
            window.removeEventListener('storage', syncUser);
        };
    }, []);

    const setColorMode = useCallback(
        (next) => {
            const resolved = next === 'dark' ? 'dark' : 'light';
            setMode(resolved);
            writeStoredColorMode(resolved, userKey);
        },
        [userKey]
    );

    const toggleColorMode = useCallback(() => {
        setColorMode(mode === 'dark' ? 'light' : 'dark');
    }, [mode, setColorMode]);

    const theme = useMemo(() => createAppTheme(mode), [mode]);

    const value = useMemo(
        () => ({
            mode,
            isDark: mode === 'dark',
            toggleColorMode,
            setColorMode,
        }),
        [mode, toggleColorMode, setColorMode]
    );

    return (
        <ColorModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ColorModeContext.Provider>
    );
}
